import { useState, type KeyboardEvent } from 'react';
import { X } from 'lucide-react';

import { t, type UiLangCode } from '../lib/i18n';
import type { Receipt } from '../lib/types';

interface TagsNotesProps {
  receipt: Receipt;
  lang: UiLangCode;
  onEdit: (next: Receipt) => void;
}

/**
 * Free-text tag chips + multi-line notes for a parsed receipt. Both are
 * pure user-supplied metadata (no OCR involvement, no FieldValue
 * wrapper) and end up in CSV/XLSX exports so a bookkeeping user can
 * filter their workbook on tags.
 *
 * Tag editing follows the standard chip-input UX: Enter or comma
 * commits the typed value as a new chip; Backspace on an empty input
 * pops the last chip; an × on each chip removes it directly.
 *
 * Notes editing commits on blur (matches the inline-editor convention
 * the receipt summary fields use).
 */
export function TagsNotes({ receipt, lang, onEdit }: TagsNotesProps) {
  const tags = receipt.tags ?? [];
  const notes = receipt.notes ?? '';
  const [draft, setDraft] = useState('');

  const commitTags = (next: string[]): void => {
    const updated: Receipt = { ...receipt };
    if (next.length === 0) delete updated.tags;
    else updated.tags = next;
    onEdit(updated);
  };

  const addTag = (raw: string): void => {
    const trimmed = raw.trim();
    if (trimmed === '') return;
    // Don't add a duplicate of an existing tag (case-insensitive
    // compare so "Travel" and "travel" don't both end up in the list).
    if (tags.some((t) => t.toLowerCase() === trimmed.toLowerCase())) {
      setDraft('');
      return;
    }
    commitTags([...tags, trimmed]);
    setDraft('');
  };

  const removeTag = (idx: number): void => {
    commitTags(tags.filter((_, i) => i !== idx));
  };

  const onTagKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag(draft);
      return;
    }
    if (e.key === 'Backspace' && draft === '' && tags.length > 0) {
      e.preventDefault();
      removeTag(tags.length - 1);
    }
  };

  const onNotesBlur = (e: React.FocusEvent<HTMLTextAreaElement>): void => {
    const next = e.currentTarget.value;
    if (next === notes) return;
    const updated: Receipt = { ...receipt };
    if (next.trim() === '') delete updated.notes;
    else updated.notes = next;
    onEdit(updated);
  };

  const labelClass =
    'mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400';

  return (
    <div className="space-y-3">
      <div>
        <span className={labelClass}>{t(lang, 'tags.label')}</span>
        <div className="flex flex-wrap items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2 py-1.5 transition-colors focus-within:border-indigo-500 focus-within:ring-1 focus-within:ring-indigo-500 dark:border-slate-700 dark:bg-slate-800 dark:focus-within:border-indigo-400 dark:focus-within:ring-indigo-400">
          {tags.map((tag, idx) => (
            <span
              key={`${tag}-${idx}`}
              className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700 ring-1 ring-inset ring-indigo-600/20 dark:bg-indigo-500/10 dark:text-indigo-300 dark:ring-indigo-500/30"
            >
              {tag}
              <button
                type="button"
                onClick={() => removeTag(idx)}
                aria-label={t(lang, 'tags.removeAria', { tag })}
                className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-full text-indigo-500 transition-colors hover:bg-indigo-200/60 hover:text-indigo-800 dark:text-indigo-400 dark:hover:bg-indigo-500/30 dark:hover:text-indigo-100"
              >
                <X className="h-2.5 w-2.5" aria-hidden="true" />
              </button>
            </span>
          ))}
          <input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onTagKeyDown}
            onBlur={() => addTag(draft)}
            placeholder={tags.length === 0 ? t(lang, 'tags.placeholder') : ''}
            aria-label={t(lang, 'tags.label')}
            className="min-w-[6rem] flex-1 border-none bg-transparent px-1 py-0.5 text-sm text-slate-900 placeholder-slate-400 outline-none focus:outline-none focus:ring-0 dark:text-slate-100 dark:placeholder-slate-500"
          />
        </div>
      </div>
      <div>
        <span className={labelClass}>{t(lang, 'notes.label')}</span>
        <textarea
          key={notes}
          defaultValue={notes}
          onBlur={onNotesBlur}
          placeholder={t(lang, 'notes.placeholder')}
          aria-label={t(lang, 'notes.label')}
          rows={2}
          className="w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm text-slate-900 placeholder-slate-400 transition-colors focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:placeholder-slate-500 dark:focus:border-indigo-400 dark:focus:ring-indigo-400"
        />
      </div>
    </div>
  );
}
